import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Store, Lock, Unlock, Plus, Trash2, Settings2, AlertTriangle, Info, Warehouse, Building2 } from "lucide-react";

export default function ShopSettings() {
  const { user } = useAuth();
  const isMaster = user?.role === "master" || user?.role === "super_user";
  
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newShopName, setNewShopName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [updating, setUpdating] = useState({});

  const fetchShops = async () => {
    try {
      const { data } = await api.get("/shops");
      setShops(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
  }, []);

  const addShop = async () => {
    if (!newShopName.trim()) return toast.error("Shop name is required");
    setSaving(true);
    try {
      await api.post("/shops", { name: newShopName.trim() });
      toast.success(`Added "${newShopName.trim()}"`);
      setNewShopName("");
      setAddDialogOpen(false);
      fetchShops();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteShop = async (shop) => {
    try {
      await api.delete(`/shops/${shop.id}`);
      toast.success(`Deleted "${shop.name}"`);
      setDeleteConfirm(null);
      fetchShops();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleStockLock = async (shop) => {
    const newValue = !shop.stock_lock;
    setUpdating((prev) => ({ ...prev, [shop.id]: true }));
    try {
      await api.patch(`/shops/${shop.id}`, { stock_lock: newValue });
      toast.success(
        newValue
          ? `Stock Lock ENABLED for ${shop.name}`
          : `Stock Lock DISABLED for ${shop.name}`
      );
      fetchShops();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUpdating((prev) => ({ ...prev, [shop.id]: false }));
    }
  };

  const updateShopType = async (shop, newType) => {
    setUpdating((prev) => ({ ...prev, [shop.id]: true }));
    try {
      await api.patch(`/shops/${shop.id}`, { shop_type: newType });
      toast.success(`${shop.name} is now a ${newType === "warehouse" ? "Warehouse" : "Normal"} shop`);
      fetchShops();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUpdating((prev) => ({ ...prev, [shop.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px] text-muted-foreground">
        Loading shops...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shop-settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl tracking-tight flex items-center gap-2">
            <Settings2 className="w-6 h-6 text-[#9E6D5B]" />
            Shop Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your shops and configure shop-specific settings
          </p>
        </div>
        {isMaster && (
          <Button
            onClick={() => setAddDialogOpen(true)}
            style={{ backgroundColor: "#9E6D5B" }}
            data-testid="add-shop-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Shop
          </Button>
        )}
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50/50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-blue-900">About Stock Lock</p>
              <p className="text-blue-700 mt-1">
                When <strong>Stock Lock</strong> is enabled for a shop, staff cannot bill products that are out of stock.
                This is useful for shops where stock verification is complete. Shops with incomplete stock entry should keep Stock Lock <strong>disabled</strong>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shops Grid */}
      {shops.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Store className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground">No shops created yet.</p>
            {isMaster && (
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setAddDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create your first shop
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {shops.map((shop) => (
            <Card
              key={shop.id}
              className={`transition-all ${shop.stock_lock ? "ring-2 ring-amber-400/50" : ""}`}
              data-testid={`shop-card-${shop.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      shop.stock_lock ? "bg-amber-100" : "bg-[#9E6D5B]/10"
                    }`}>
                      {shop.stock_lock ? (
                        <Lock className="w-5 h-5 text-amber-600" />
                      ) : (
                        <Store className="w-5 h-5 text-[#9E6D5B]" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{shop.name}</CardTitle>
                      <CardDescription className="text-xs mt-0.5">
                        {shop.slug || shop.name.toLowerCase().replace(/\s+/g, "-")}
                      </CardDescription>
                    </div>
                  </div>
                  {isMaster && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setDeleteConfirm(shop)}
                      data-testid={`delete-shop-${shop.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Stock Lock Toggle */}
                <div className={`rounded-lg p-4 ${
                  shop.stock_lock ? "bg-amber-50 border border-amber-200" : "bg-slate-50 border border-slate-200"
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {shop.stock_lock ? (
                        <Lock className="w-4 h-4 text-amber-600" />
                      ) : (
                        <Unlock className="w-4 h-4 text-slate-500" />
                      )}
                      <Label htmlFor={`stock-lock-${shop.id}`} className="font-medium cursor-pointer">
                        Stock Lock
                      </Label>
                    </div>
                    {isMaster ? (
                      <Switch
                        id={`stock-lock-${shop.id}`}
                        checked={shop.stock_lock || false}
                        onCheckedChange={() => toggleStockLock(shop)}
                        disabled={updating[shop.id]}
                        data-testid={`stock-lock-toggle-${shop.id}`}
                      />
                    ) : (
                      <span className={`text-xs font-medium px-2 py-1 rounded ${
                        shop.stock_lock
                          ? "bg-amber-200 text-amber-800"
                          : "bg-slate-200 text-slate-600"
                      }`}>
                        {shop.stock_lock ? "ENABLED" : "DISABLED"}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {shop.stock_lock
                      ? "Products without stock cannot be billed"
                      : "Any product can be billed regardless of stock"}
                  </p>
                </div>

                {/* Stock Lock Warning */}
                {shop.stock_lock && (
                  <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-amber-50/50 border border-amber-100">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-800">
                      <strong>Active:</strong> Staff must have stock to bill products at this shop
                    </p>
                  </div>
                )}

                {/* Shop Type */}
                <div className={`rounded-lg p-4 mt-3 ${
                  shop.shop_type === "warehouse" ? "bg-blue-50 border border-blue-200" : "bg-slate-50 border border-slate-200"
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {shop.shop_type === "warehouse" ? (
                        <Warehouse className="w-4 h-4 text-blue-600" />
                      ) : (
                        <Building2 className="w-4 h-4 text-slate-500" />
                      )}
                      <Label className="font-medium">Shop Type</Label>
                    </div>
                    {isMaster ? (
                      <Select
                        value={shop.shop_type || "normal"}
                        onValueChange={(v) => updateShopType(shop, v)}
                        disabled={updating[shop.id]}
                      >
                        <SelectTrigger className="w-[130px] h-8" data-testid={`shop-type-select-${shop.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="warehouse">Warehouse</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`text-xs font-medium px-2 py-1 rounded ${
                        shop.shop_type === "warehouse"
                          ? "bg-blue-200 text-blue-800"
                          : "bg-slate-200 text-slate-600"
                      }`}>
                        {shop.shop_type === "warehouse" ? "WAREHOUSE" : "NORMAL"}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {shop.shop_type === "warehouse"
                      ? "Higher stock thresholds - functions as stock-holding location"
                      : "Standard retail shop with normal stock thresholds"}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Shop Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-shop-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <Store className="w-5 h-5 text-[#9E6D5B]" />
              Add New Shop
            </DialogTitle>
            <DialogDescription>
              Create a new shop location. You can configure Stock Lock after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="shop-name">Shop Name</Label>
              <Input
                id="shop-name"
                value={newShopName}
                onChange={(e) => setNewShopName(e.target.value)}
                placeholder="e.g., Main Branch, Karette..."
                onKeyDown={(e) => e.key === "Enter" && addShop()}
                data-testid="new-shop-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={addShop}
              disabled={saving || !newShopName.trim()}
              style={{ backgroundColor: "#9E6D5B" }}
              data-testid="confirm-add-shop-btn"
            >
              {saving ? "Adding..." : "Add Shop"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent data-testid="delete-shop-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shop?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>?
              <br /><br />
              This action cannot be undone. Shops with sales, expenses, incomes, or assigned users cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteShop(deleteConfirm)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-shop-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
